// src/api/messaging/MessagesApi.ts
import { ENV } from '../../config/runtimeEnv';
import CommunicationsService from '../../services/CommunicationsService';
import {
	Message,
	SendMessageRequest,
	EditMessageRequest,
	DeleteMessageRequest,
	MarkAsReadRequest,
	GetMessagesParams,
	MessageListResponse,
	MessageReadReceipt,
	ApiResponse,
	MessageAttachment,
} from '../../types/messaging';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

const createApiInstance = () => {
	const socketId = CommunicationsService.getSocketId();
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
	};
	if (socketId) {
		headers['X-Socket-ID'] = socketId;
	}
	return createServiceClient({ baseURL: API_BASE_URL, headers });
};

const createMultipartApiInstance = () => {
	const socketId = CommunicationsService.getSocketId();
	const headers: Record<string, string> = {
		'Content-Type': 'multipart/form-data',
	};
	if (socketId) {
		headers['X-Socket-ID'] = socketId;
	}
	return createServiceClient({ baseURL: API_BASE_URL, headers });
};

export const getMessages = async (conversationId: string, params: GetMessagesParams): Promise<Message[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<MessageListResponse>(
			`/conversations/${conversationId}/messages`,
			{ params }
		);
		return response.data.data;
	} catch (error) {
		console.error('Error fetching messages:', error);
		throw error;
	}
};

export const sendMessage = async (conversationId: string, request: SendMessageRequest): Promise<Message> => {
	try {
		const hasFiles = request.files && request.files.length > 0;

		if (hasFiles) {
			const api = createMultipartApiInstance();
			const formData = new FormData();

			formData.append('sender_user_id', request.sender_user_id.toString());
			formData.append('content', request.content);

			if (request.type) {
				formData.append('type', request.type);
			}

			if (request.reply_to_message_id) {
				formData.append('reply_to_message_id', request.reply_to_message_id);
			}

			request.files.forEach((file) => {
				formData.append('files[]', file);
			});

			const response = await api.post<ApiResponse<Message>>(
				`/conversations/${conversationId}/messages`,
				formData
			);
			return response.data.data!;
		} else {
			const api = createApiInstance();
			const response = await api.post<ApiResponse<Message>>(
				`/conversations/${conversationId}/messages`,
				{
					sender_user_id: request.sender_user_id,
					content: request.content,
					type: request.type,
					reply_to_message_id: request.reply_to_message_id,
				}
			);
			return response.data.data!;
		}
	} catch (error) {
		console.error('Error sending message:', error);
		throw error;
	}
};

export const editMessage = async (messageId: string, request: EditMessageRequest): Promise<Message> => {
	try {
		const api = createApiInstance();
		const response = await api.put<ApiResponse<Message>>(`/messages/${messageId}`, request);
		return response.data.data!;
	} catch (error) {
		console.error('Error editing message:', error);
		throw error;
	}
};

export const deleteMessage = async (messageId: string, request: DeleteMessageRequest): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.delete(`/messages/${messageId}`, { data: request });
	} catch (error) {
		console.error('Error deleting message:', error);
		throw error;
	}
};

export const markMessageAsRead = async (
	conversationId: string,
	messageId: string,
	request: MarkAsReadRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/messages/${conversationId}/${messageId}/read`, request);
	} catch (error) {
		console.error('Error marking message as read:', error);
		throw error;
	}
};

export const markConversationAsRead = async (
	conversationId: string,
	request: MarkAsReadRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/messages/mark-read`, request);
	} catch (error) {
		console.error('Error marking conversation as read:', error);
		throw error;
	}
};

export const getReadReceipts = async (messageId: string, userId: UserUuid): Promise<MessageReadReceipt[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ApiResponse<MessageReadReceipt[]>>(`/messages/${messageId}/receipts`, {
			params: { user_id: userId },
		});
		return response.data.data || [];
	} catch (error) {
		console.error('Error fetching read receipts:', error);
		throw error;
	}
};

export const getMessageAttachments = async (messageId: string, userId: UserUuid): Promise<MessageAttachment[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ApiResponse<MessageAttachment[]>>(
			`/messaging/conversations/messages/${messageId}/attachments`,
			{ params: { user_id: userId } }
		);
		return response.data.data || [];
	} catch (error) {
		console.error('Error fetching attachments:', error);
		throw error;
	}
};

export const downloadAttachment = async (attachmentUrl: string): Promise<void> => {
	try {
		window.open(attachmentUrl, '_blank');
	} catch (error) {
		console.error('Error downloading attachment:', error);
		throw error;
	}
};

export const loadMoreMessages = async (
	conversationId: string,
	userId: UserUuid,
	beforeMessageId: string,
	limit: number = 50
): Promise<Message[]> => {
	return getMessages(conversationId, {
		user_id: userId,
		limit,
		before_message_id: beforeMessageId,
	});
};

export const formatMessageContent = (content: string | null, maxLength: number = 100): string => {
	if (!content) return '';

	const stripped = content.replace(/<[^>]*>/g, '');

	if (stripped.length <= maxLength) {
		return stripped;
	}

	return stripped.substring(0, maxLength) + '...';
};

export const groupMessagesByDate = (messages: Message[]): Record<string, Message[]> => {
	return messages.reduce((groups, message) => {
		const date = new Date(message.created_at).toLocaleDateString();
		if (!groups[date]) {
			groups[date] = [];
		}
		groups[date].push(message);
		return groups;
	}, {} as Record<string, Message[]>);
};

export const isOwnMessage = (message: Message, userId: UserUuid): boolean => {
	return message.sender_user_id === userId;
};

export const getTimeAgo = (dateString: string): string => {
	const date = new Date(dateString);
	const now = new Date();
	const diff = now.getTime() - date.getTime();
	const minutes = Math.floor(diff / 60000);
	const hours = Math.floor(diff / 3600000);
	const days = Math.floor(diff / 86400000);

	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;
	if (hours < 24) return `${hours}h ago`;
	if (days < 7) return `${days}d ago`;
	return date.toLocaleDateString();
};

export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getFileIcon = (mimeType: string): string => {
	if (mimeType.startsWith('image/')) return '🖼️';
	if (mimeType.startsWith('video/')) return '🎥';
	if (mimeType.startsWith('audio/')) return '🎵';
	if (mimeType.includes('pdf')) return '📄';
	if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
	if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
	if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
	if (mimeType.includes('zip') || mimeType.includes('compressed')) return '📦';
	return '📎';
};